// frontend/src/hooks/useWebSocket.js

import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth } from './useAuth'

export const useWebSocket = (onMessage) => {
  const { token } = useAuth()
  const ws = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = useRef(1000)

  const connect = useCallback(() => {
    if (!token) {
      console.warn('⚠️  Токен не найден, WebSocket не подключен')
      return
    }

    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${wsProtocol}//${window.location.host}/api/websocket/ws?token=${token}`

      console.log('🔌 Подключение к WebSocket:', wsUrl)

      ws.current = new WebSocket(wsUrl)

      ws.current.onopen = () => {
        console.log('✅ WebSocket подключен')
        setIsConnected(true)
        setError(null)
        reconnectAttempts.current = 0
        reconnectDelay.current = 1000
      }

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          console.log('📨 Сообщение WebSocket:', message)

          if (onMessage) {
            onMessage(message)
          }
        } catch (e) {
          console.error('❌ Ошибка парсинга WebSocket сообщения:', e)
        }
      }

      ws.current.onerror = (error) => {
        console.error('❌ Ошибка WebSocket:', error)
        setError('WebSocket ошибка')
        setIsConnected(false)
      }

      ws.current.onclose = () => {
        console.log('🔌 WebSocket отключен')
        setIsConnected(false)

        // Попытка переподключения
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1
          const delay = reconnectDelay.current * Math.pow(2, reconnectAttempts.current - 1)
          console.log(`⏳ Попытка переподключения ${reconnectAttempts.current}/${maxReconnectAttempts} через ${delay}ms`)

          setTimeout(() => {
            connect()
          }, delay)
        } else {
          setError('Не удалось подключиться к WebSocket')
        }
      }
    } catch (e) {
      console.error('❌ Ошибка при создании WebSocket:', e)
      setError(e.message)
    }
  }, [token, onMessage])

  useEffect(() => {
    connect()

    return () => {
      if (ws.current) {
        ws.current.close()
      }
    }
  }, [connect])

  const send = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message))
    } else {
      console.warn('⚠️  WebSocket не подключен')
    }
  }, [])

  const ping = useCallback(() => {
    send({
      type: 'ping',
      timestamp: Date.now()
    })
  }, [send])

  return {
    isConnected,
    error,
    send,
    ping,
    ws: ws.current
  }
}
